import type { PaletteMode } from '@mui/material';
import set from 'lodash/set.js';
import {
  type PanwDictionaryColorTokenName,
  type UnitySemanticColorTokenName,
  unitySemanticDarkColorTokens,
  unitySemanticLightColorTokens,
  type XcorDefinedColorTokenName,
  xcorDefinedDarkTokens,
  xcorDefinedLightTokens,
} from '../../unityTokens';

const NON_COLOR_PALETTE_TOKENS = {
  /** `buildPaletteFromMetadata` assigns a new `{}` for each path to avoid shared mutable reference */
  'empty-object': {},
  unset: 'unset',
  undefined: undefined,
  transparent: 'transparent',
} as const;

export type NonColorPaletteTokenValue = keyof typeof NON_COLOR_PALETTE_TOKENS;

export type NonColorPaletteTokenMarker = (typeof NON_COLOR_PALETTE_TOKENS)[NonColorPaletteTokenValue];

/**
 * Semantic/XCOR dictionary key or a non color marker resolved by `buildPaletteFromMetadata`.
 */
export type PaletteTokenName = PanwDictionaryColorTokenName | NonColorPaletteTokenMarker;

const NON_COLOR_PALETTE_MARKERS = new Set(Object.values(NON_COLOR_PALETTE_TOKENS));

/** Whether `tokenName` is a non color palette marker, not a semantic/XCOR dictionary key. */
export function isNonColorPaletteTokenMarker(tokenName: PaletteTokenName): tokenName is NonColorPaletteTokenMarker {
  return NON_COLOR_PALETTE_MARKERS.has(tokenName);
}

/**
 * Maps a non color palette value to the internal token name consumed by `buildPaletteFromMetadata`.
 */
export function nonColorPaletteToken(kind: NonColorPaletteTokenValue): NonColorPaletteTokenMarker {
  return NON_COLOR_PALETTE_TOKENS[kind];
}

/**
 * Metadata that maps palette property paths to their token names.
 * This preserves the source-of-truth relationship between palettes and tokens.
 */
export interface Token {
  /** The palette property path (e.g., 'standard.primary.default' or 'checkedColor') */
  propertyPath: string;
  /** The semantic or XCOR token name (e.g. `--semantic-bg-interactive-highlight-default`), or a value from `NON_COLOR_PALETTE_TOKENS` */
  tokenName: PaletteTokenName;
}

/** Token whose tokenName is a color key (not a non-color marker). */
export interface ColorPaletteToken extends Omit<Token, 'tokenName'> {
  tokenName: PanwDictionaryColorTokenName;
}

/** True when `token.tokenName` is a color token (semantic / XCOR lookup) */
export function isColorPaletteToken(token: Token): token is ColorPaletteToken {
  return !isNonColorPaletteTokenMarker(token.tokenName);
}

export interface PaletteTokenMetadata {
  /** Component name (e.g., 'button', 'input') */
  componentName: string;
  /** Maps palette property paths → token names (resolved per theme mode via semantic / XCOR dictionaries). */
  tokens: Token[];
}

/**
 * Build a palette object from metadata by resolving token names to values
 * and constructing nested objects from dot-notation paths.
 *
 * @param metadata - Array of tokens with property paths and token names
 * @param tokens - Primary token dictionary (semantic tokens)
 * @param xcorTokens - Optional XCOR token dictionary for component-specific overrides
 * @returns Palette object with resolved color values
 */
export function buildPaletteFromMetadata<T>(
  metadata: Token[],
  tokens: Record<UnitySemanticColorTokenName, string>,
  /** Light and dark XCOR maps each define a subset of XcorDefinedColorTokenNames */
  xcorTokens?: Partial<Record<XcorDefinedColorTokenName, string>>
): T {
  const result: Record<string, unknown> = {};
  for (const token of metadata) {
    // Create new object for empty-object to avoid shared mutable reference
    const nonPaletteTokenValue = token.tokenName === NON_COLOR_PALETTE_TOKENS['empty-object'] ? {} : token.tokenName;
    const value = isNonColorPaletteTokenMarker(token.tokenName)
      ? nonPaletteTokenValue
      : ((tokens as Record<string, string>)[token.tokenName] ??
        (xcorTokens as Record<string, string>)?.[token.tokenName]);
    set(result, token.propertyPath, value);
  }
  return result as T;
}

/**
 * Helper to build a palette from metadata with mode-aware token resolution.
 * Handles the boilerplate of selecting light/dark token dictionaries and calling buildPaletteFromMetadata.
 */
export function buildPaletteForMode<T>(tokens: Token[] | ((mode: PaletteMode) => Token[]), mode: PaletteMode): T {
  const semanticTokens = mode === 'light' ? unitySemanticLightColorTokens : unitySemanticDarkColorTokens;
  const xcorTokens = mode === 'light' ? xcorDefinedLightTokens : xcorDefinedDarkTokens;
  const resolvedTokens = typeof tokens === 'function' ? tokens(mode) : tokens;
  return buildPaletteFromMetadata<T>(resolvedTokens, semanticTokens, xcorTokens);
}
